import React, { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import Root from "./root";
import SelectProjectType from "../pages/Projects/SelectProjectType";
import CreateDomain from "../pages/Domains/CreateDomain";
import EditDomain from "../pages/Domains/EditDomain";
import CreateProject from "../pages/Projects/CreateProject";
import EditProject from "../pages/Projects/EditProject";
import ViewProject from "../pages/Projects/ViewProject";
import CreateTutorial from "../pages/Tutorials/CreateTutorial";
import EditTutorial from "../pages/Tutorials/EditTutorial";
import CreateAgency from "../pages/Agencies/CreateAgency";
import EditAgency from "../pages/Agencies/EditAgency";
import ResetPasswordForm from "../pages/ResetPassword/ResetPasswordForm";
import LoaderSpinner from "../components/LoaderSpinner";
import Notification from "../pages/Notification/Notification";

// Chunk load failures happen when the user's tab has an old index.html pointing
// at chunk hashes that got rsync-deleted during a deploy. Reload once to pick up
// the fresh index.html; the sessionStorage flag stops an infinite reload loop.
const lazyWithRetry = (importer) =>
  lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        /Loading chunk .* failed/i.test(err?.message || "");
      if (isChunkError) {
        const key = "chunk-reload-at";
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
          return { default: () => null };
        }
      }
      throw err;
    }
  });

const Login = lazyWithRetry(() => import("../pages/Login"));
const SignUp = lazyWithRetry(() => import("../pages/SignUp"));
const Dashboard = lazyWithRetry(() => import("../pages/Dashboard"));
const Projects = lazyWithRetry(() => import("../pages/Projects"));
const Domains = lazyWithRetry(() => import("../pages/Domains"));
const MyAgency = lazyWithRetry(() => import("../pages/MyAgency"));
const Tutorials = lazyWithRetry(() => import("../pages/Tutorials"));
const Resources = lazyWithRetry(() => import("../pages/Resources"));
const Settings = lazyWithRetry(() => import("../pages/Settings"));
const Integrations = lazyWithRetry(() => import("../pages/Integrations"));
const PaymentMethods = lazyWithRetry(() => import("../pages/PaymentMethods"));
const Reporting = lazyWithRetry(() => import("../pages/Reporting"));
const ResetPassword = lazyWithRetry(() => import("../pages/ResetPassword"));
const ScanReporting = lazyWithRetry(() => import("../pages/ScanReporting"));
const NewScan = lazyWithRetry(() => import("../pages/NewScan"));
const AIVisibility = lazyWithRetry(() => import("../pages/AIVisibility"));
const AIVisibilityScanDetail = lazyWithRetry(() => import("../pages/AIVisibility/ScanDetail"));
const KeywordResearch = lazyWithRetry(() => import("../pages/KeywordResearch"));
const KeywordResearchDetail = lazyWithRetry(() => import("../pages/KeywordResearch/Detail"));
const StripeProducts = lazyWithRetry(() => import("../pages/StripeProducts"));
const StripeSubscriptions = lazyWithRetry(() => import("../pages/StripeSubscriptions"));
const Agencies = lazyWithRetry(() => import("../pages/Agencies"));
const AllUsers = lazyWithRetry(() => import("../pages/AllUsers"));
const ErrorPage = lazyWithRetry(() => import("../router/ErrorPage.js"));

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Login />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Login />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/registration",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <SignUp />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/password/reset",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <ResetPassword />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/password-reset",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <ResetPasswordForm />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },

  {
    path: "/dashboard",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Dashboard />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/projects",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Projects />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/projects/select-project-type",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<SelectProjectType />} />
      </Suspense>
    ),
  },
  {
    path: "/projects/create-project",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<CreateProject />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/projects/:id",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<ViewProject />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/projects/:id/edit",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<EditProject />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/domains",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Domains />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/add-domain",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<CreateDomain />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/domains/:id/edit",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<EditDomain />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/domains/add-domain",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<CreateDomain />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/reporting",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Reporting />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/reporting/scan-reporting",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<ScanReporting />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/reporting/scan/:id",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<ScanReporting />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/reporting/new-scan",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<NewScan />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/ai-visibility",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        <Root component={<AIVisibility />} />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/ai-visibility/:id",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        <Root component={<AIVisibilityScanDetail />} />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/keyword-research",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        <Root component={<KeywordResearch />} />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/keyword-research/:id",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        <Root component={<KeywordResearchDetail />} />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  // Resources route disabled — Airtable embed deprecated. Re-enable by uncommenting.
  // {
  //   path: "/resources",
  //   element: (
  //     <Suspense fallback={<LoaderSpinner />}>
  //       {" "}
  //       <Root component={<Resources />} />,
  //     </Suspense>
  //   ),
  //   errorElement: <ErrorPage />,
  // },
  {
    path: "/scan-reporting",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<ScanReporting />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/integrations",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Integrations />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/tutorials",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Tutorials />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/tutorials/create",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<CreateTutorial />} />
      </Suspense>
    ),
  },
  {
    path: "/tutorials/:id/edit",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<EditTutorial />} />
      </Suspense>
    ),
  },
  {
    path: "/settings",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Settings />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/my-agency",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<MyAgency />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/agencies",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Agencies />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/agencies/create",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<CreateAgency />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/agencies/:id/edit",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<EditAgency />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/stripe/products",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<StripeProducts />} />
      </Suspense>
    ),
  },
  {
    path: "/stripe/all-subscriptions",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<StripeSubscriptions />} />
      </Suspense>
    ),
  },
  {
    path: "/payment",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<PaymentMethods />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/stripe/all-users",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<AllUsers />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/notifications/:id",
    element: (
      <Suspense fallback={<LoaderSpinner />}>
        {" "}
        <Root component={<Notification />} />,
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
]);

export default router;
